import { useCallback, useState } from 'react';
import { captureSupabaseError } from '../lib/supabaseError';

import { axiosRubyClient } from '@/util/axios/axiosClient';

import type { MessageType } from '../types';

interface SendMessageParams {
  conversationId: string;
  content: string;
  messageType?: Extract<MessageType, 'text' | 'image'>;
  metadata?: Record<string, unknown>;
  inReplyToBroadcastId?: string;
}

interface SendMessageResponse {
  success: boolean;
  data: {
    message_id: string;
    created_at: string;
  };
  errors?: string[];
}

export function useSendMessage() {
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = useCallback(
    async ({
      conversationId,
      content,
      messageType = 'text',
      metadata,
      inReplyToBroadcastId,
    }: SendMessageParams) => {
      const trimmed = content.trim();
      if (!trimmed) throw new Error('Message is empty');

      setSending(true);
      setError(null);
      try {
        const body: Record<string, unknown> = {
          content: trimmed,
          message_type: messageType,
        };
        if (metadata) body.metadata = metadata;
        if (inReplyToBroadcastId) {
          body.in_reply_to_broadcast_id = inReplyToBroadcastId;
        }
        const res = await axiosRubyClient.post<SendMessageResponse>(
          `/api/admin/conversations/${conversationId}/messages`,
          body,
        );
        return res.data.data;
      } catch (err: unknown) {
        const message =
          (
            err as { response?: { data?: { errors?: string[] } } }
          )?.response?.data?.errors?.join(', ') ??
          (err as Error)?.message ??
          'Failed to send message';
        captureSupabaseError(err, {
          action: 'send_message',
          extra: { conversation_id: conversationId },
        });
        setError(message);
        throw err;
      } finally {
        setSending(false);
      }
    },
    [],
  );

  return { send, sending, error };
}
